/**
 * B2BOrderRMAPage — Phase 66: Order-Based RMA
 *
 * Route: /b2b/orders/:orderId/rma  (nested under B2BLayout)
 *
 * Order-driven flow (replaces the legacy blind-search page in user-facing UX):
 *
 *   1. User opens this page from "ההזמנות שלי" → "בקש החזרה" on a delivered order.
 *   2. Page calls RPC public.get_order_for_rma(order_id), returning the order
 *      header + every order_line annotated with already_returned_qty and
 *      max_returnable_qty.
 *   3. The user picks a return-qty per line (capped at max_returnable_qty)
 *      and an optional per-line reason + global reason.
 *   4. On submit, we call submit_rma() with p_source_order = order.id and a
 *      lines payload of { order_line_id, requested_qty, return_reason }.
 *   5. The backend auto-fills unit_price from the *historical* b2b_order_lines
 *      row (the price the customer actually paid) — so the credit is computed
 *      against the price paid, NOT the current catalog price.
 *
 * The page only allows orders in the "delivered/completed" state — for
 * b2b_orders that's status = 'locked'. Anything else is rejected by the
 * backend RPC and we surface that gracefully.
 *
 * Theme: warm-orange (matches the legacy returns page) to signal "return"
 * context distinctly from regular sales.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link, Navigate }    from 'react-router-dom';
import { supabase }                                  from '@/lib/supabase';
import { useB2BUser }                                from './B2BUserContext';
import { useB2BTerms } from './B2BTermsContext';
import { Loader as Loader2, CircleAlert as AlertCircle, RotateCcw, ArrowRight, CircleCheck as CheckCircle2, Package, Send, MessageSquare, Calendar, Hash, ShoppingBag, Info } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RMAOrderLine {
  order_line_id:        string;
  product_id:           string;
  product_name:         string;
  product_sku:          string;
  product_image_url:    string | null;
  requested_quantity:   number;
  approved_quantity:    number;
  unit_price:           number;
  total_line_price:     number;
  already_returned_qty: number;
  max_returnable_qty:   number;
}

interface RMAOrderHeader {
  order_id:           string;
  order_status:       string;
  order_created_at:   string;
  order_total_amount: number;
  company_id:         string;
  company_name:       string | null;
  client_id:          string;
}

interface OrderForRMA {
  header: RMAOrderHeader;
  lines:  RMAOrderLine[];
}

interface ReturnSelection {
  // keyed by order_line_id
  qty:    number;
  reason: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(n: number) {
  return `₪${n.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('he-IL', {
    day:   '2-digit',
    month: 'short',
    year:  'numeric',
    hour:  '2-digit',
    minute:'2-digit',
  });
}

function shortId(id: string) {
  return id.slice(0, 8).toUpperCase();
}

// ─── Product image ────────────────────────────────────────────────────────────

function ProductImage({ src, alt }: { src: string | null; alt: string }) {
  if (!src) {
    return (
      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
        <Package className="w-5 h-5 text-gray-300" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-12 h-12 rounded-xl object-cover shrink-0 bg-gray-100"
      loading="lazy"
      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
    />
  );
}

// ─── Return-qty input ─────────────────────────────────────────────────────────
//
// Special "0 = don't return" semantics. Capped at max_returnable_qty.

function ReturnQtyInput({
  value,
  max,
  onChange,
  disabled,
}: {
  value:    number;
  max:      number;
  onChange: (n: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min={0}
        max={max}
        step={1}
        value={value}
        disabled={disabled}
        onChange={e => {
          const raw = e.target.value;
          if (raw === '') { onChange(0); return; }
          const n = parseInt(raw, 10);
          if (isNaN(n)) return;
          onChange(Math.max(0, Math.min(max, n)));
        }}
        className="w-16 h-8 text-center text-sm font-bold border border-orange-200
                   rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300
                   text-orange-700 bg-orange-50/50 tabular-nums
                   disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="כמות להחזרה"
      />
      <span className="text-[10px] text-gray-400 whitespace-nowrap">
        / {max}
      </span>
    </div>
  );
}

// ─── Per-line row ─────────────────────────────────────────────────────────────

function RMALineRow({
  line,
  qty,
  reason,
  onQtyChange,
  onReasonChange,
}: {
  line:           RMAOrderLine;
  qty:            number;
  reason:         string;
  onQtyChange:    (lineId: string, qty: number) => void;
  onReasonChange: (lineId: string, reason: string) => void;
}) {
  const [showReason, setShowReason] = useState(false);
  const fullyReturned = line.max_returnable_qty <= 0;
  const partial       = line.already_returned_qty > 0 && !fullyReturned;
  const lineCredit    = qty * Number(line.unit_price);

  return (
    <div
      className={`rounded-xl border p-3 space-y-2.5 transition-colors
                  ${qty > 0
                    ? 'border-orange-300 bg-orange-50/60'
                    : fullyReturned
                      ? 'border-gray-200 bg-gray-50 opacity-60'
                      : 'border-orange-100 bg-white'
                  }`}
    >
      {/* Top row: product info + return-qty input */}
      <div className="flex items-start gap-3">
        <ProductImage src={line.product_image_url} alt={line.product_name} />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight">
            {line.product_name}
          </p>
          <p className="text-[10px] text-gray-400 font-mono mt-0.5" dir="ltr">
            {line.product_sku}
          </p>

          {/* Purchased / paid line */}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span className="text-[11px] text-gray-500">
              נרכש: <span className="font-semibold text-gray-700 tabular-nums">
                {line.approved_quantity.toLocaleString('he-IL')}
              </span>
            </span>
            <span className="text-[11px] text-gray-500">
              מחיר ששולם:{' '}
              <span className="font-semibold text-gray-700">
                {fmtPrice(Number(line.unit_price))}
              </span>
            </span>
          </div>

          {partial && (
            <p className="mt-1 text-[10px] text-amber-600 font-semibold">
              כבר הוחזרו {line.already_returned_qty} יח׳ בעבר
            </p>
          )}
          {fullyReturned && (
            <p className="mt-1 text-[10px] text-gray-400 font-semibold">
              כל הכמות כבר הוחזרה
            </p>
          )}
        </div>

        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <ReturnQtyInput
            value={qty}
            max={line.max_returnable_qty}
            onChange={n => onQtyChange(line.order_line_id, n)}
            disabled={fullyReturned}
          />
          {qty > 0 && (
            <span className="text-[10px] font-bold text-orange-600 tabular-nums">
              זיכוי: {fmtPrice(lineCredit)}
            </span>
          )}
        </div>
      </div>

      {/* Per-line reason toggle */}
      {qty > 0 && (
        <>
          <button
            type="button"
            onClick={() => setShowReason(s => !s)}
            className={`flex items-center gap-1 text-[11px] font-medium rounded-lg
                        px-2 py-1 transition-colors
                        ${showReason || reason
                          ? 'text-orange-600 bg-orange-100'
                          : 'text-gray-400 hover:text-gray-600'
                        }`}
          >
            <MessageSquare className="w-3 h-3" />
            {showReason || reason ? 'הסתר סיבה' : 'הוסף סיבה לפריט זה'}
          </button>

          {(showReason || reason) && (
            <input
              type="text"
              value={reason}
              onChange={e => onReasonChange(line.order_line_id, e.target.value)}
              placeholder="סיבת ההחזרה לפריט זה (אופציונלי)…"
              className="w-full text-xs rounded-lg border border-orange-200 px-2.5 py-1.5
                         focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white
                         placeholder:text-gray-300"
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function B2BOrderRMAPage() {
  const { orderId }   = useParams<{ orderId: string }>();
  const navigate      = useNavigate();
  const { session }   = useB2BUser();

  // Phase 112: redirect when returns disabled for this company
  const [returnsEnabled, setReturnsEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    const companyId = session?.activeCompanyId;
    if (!companyId) return;
    supabase
      .from('companies')
      .select('b2b_returns_enabled')
      .eq('id', companyId)
      .single()
      .then(({ data }) => {
        setReturnsEnabled(data ? Boolean((data as { b2b_returns_enabled: boolean }).b2b_returns_enabled) : true);
      });
  }, [session?.activeCompanyId]);

  const [data,        setData]        = useState<OrderForRMA | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState<string | null>(null);
  const [selection,   setSelection]   = useState<Record<string, ReturnSelection>>({});
  const [globalReason, setGlobalReason] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success,     setSuccess]     = useState<{ rmaId: string } | null>(null);

  // ── Load order via get_order_for_rma RPC ─────────────────────────────────
  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data: rows, error: rpcErr } = await supabase
        .rpc('get_order_for_rma', { p_order_id: orderId });

      if (rpcErr) throw new Error(rpcErr.message);
      if (!rows || (rows as any[]).length === 0) {
        throw new Error('הזמנה זו לא נמצאה או שאין לה פריטים');
      }

      const list = rows as any[];
      const first = list[0];

      const header: RMAOrderHeader = {
        order_id:           first.order_id,
        order_status:       first.order_status,
        order_created_at:   first.order_created_at,
        order_total_amount: Number(first.order_total_amount ?? 0),
        company_id:         first.company_id,
        company_name:       first.company_name,
        client_id:          first.client_id,
      };

      // Reject orders that aren't delivered (UI guard — RPC will reject too)
      if (header.order_status !== 'locked') {
        throw new Error('ניתן לבקש החזרה רק עבור הזמנות שאושרו סופית/סופקו');
      }

      const lines: RMAOrderLine[] = list.map(r => ({
        order_line_id:        r.order_line_id,
        product_id:           r.product_id,
        product_name:         r.product_name ?? 'מוצר לא זמין',
        product_sku:          r.product_sku ?? '',
        product_image_url:    r.product_image_url ?? null,
        requested_quantity:   Number(r.requested_quantity ?? 0),
        approved_quantity:    Number(r.approved_quantity ?? 0),
        unit_price:           Number(r.unit_price ?? 0),
        total_line_price:     Number(r.total_line_price ?? 0),
        already_returned_qty: Number(r.already_returned_qty ?? 0),
        max_returnable_qty:   Number(r.max_returnable_qty ?? 0),
      }));

      setData({ header, lines });

      // Initialise selection map (qty=0 by default)
      const init: Record<string, ReturnSelection> = {};
      for (const l of lines) {
        init[l.order_line_id] = { qty: 0, reason: '' };
      }
      setSelection(init);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'שגיאה בטעינת ההזמנה');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // ── Selection mutators ───────────────────────────────────────────────────
  const setLineQty = useCallback((lineId: string, qty: number) => {
    setSelection(prev => ({
      ...prev,
      [lineId]: { ...(prev[lineId] ?? { qty: 0, reason: '' }), qty },
    }));
  }, []);

  const setLineReason = useCallback((lineId: string, reason: string) => {
    setSelection(prev => ({
      ...prev,
      [lineId]: { ...(prev[lineId] ?? { qty: 0, reason: '' }), reason },
    }));
  }, []);

  // ── Totals ───────────────────────────────────────────────────────────────
  const { selectedLines, totalQty, totalCredit } = useMemo(() => {
    const selected: { line: RMAOrderLine; qty: number; reason: string }[] = [];
    let qSum = 0;
    let cSum = 0;
    if (data) {
      for (const l of data.lines) {
        const s = selection[l.order_line_id];
        if (s && s.qty > 0) {
          selected.push({ line: l, qty: s.qty, reason: s.reason });
          qSum += s.qty;
          cSum += s.qty * Number(l.unit_price);
        }
      }
    }
    return { selectedLines: selected, totalQty: qSum, totalCredit: cSum };
  }, [data, selection]);

  const canSubmit = !submitting && !!data && selectedLines.length > 0;

  // ── Submit ───────────────────────────────────────────────────────────────
  const { remindIfNeeded: remindTerms } = useB2BTerms();
  const handleSubmit = useCallback(async () => {
    if (!data || selectedLines.length === 0) return;
    await remindTerms();
    setSubmitError(null);
    setSubmitting(true);

    try {
      const payload = selectedLines.map(({ line, qty, reason }) => ({
        order_line_id: line.order_line_id,
        requested_qty: qty,
        return_reason: reason?.trim() || null,
      }));

      const agentId = session?.role === 'agent' ? session?.agentRecord?.id ?? null : null;

      const { data: rmaId, error: rpcErr } = await supabase.rpc('submit_rma', {
        p_company_id:    data.header.company_id,
        p_client_id:     data.header.client_id,
        p_agent_id:      agentId,
        p_source_order:  data.header.order_id,
        p_return_reason: globalReason.trim() || null,
        p_lines:         payload,
      });

      if (rpcErr) throw new Error(rpcErr.message);
      if (!rmaId)  throw new Error('שרת לא החזיר מזהה החזרה');

      setSuccess({ rmaId: rmaId as unknown as string });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'שגיאה בשליחת ההחזרה');
    } finally {
      setSubmitting(false);
    }
  }, [data, selectedLines, globalReason, session, remindTerms]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (returnsEnabled === false) return <Navigate to="/b2b/orders" replace />;

  // Success screen
  if (success) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12" dir="rtl">
        <div className="bg-white rounded-3xl shadow-sm border border-green-100
                        p-8 flex flex-col items-center gap-5 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <div className="space-y-1.5">
            <h1 className="text-xl font-bold text-gray-900">בקשת ההחזרה נשלחה בהצלחה</h1>
            <p className="text-sm text-gray-500">
              צוות הספק יבדוק את הבקשה ויחזור אליך בהקדם.
            </p>
          </div>

          <div className="w-full rounded-2xl bg-gray-50 px-4 py-3 text-right space-y-1">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide">מזהה בקשה</p>
            <p className="text-sm font-mono text-gray-700">
              #{shortId(success.rmaId)}
            </p>
          </div>

          <div className="flex flex-col w-full gap-2 pt-2">
            <button
              onClick={() => navigate('/b2b/orders')}
              className="w-full flex items-center justify-center gap-2
                         py-3 rounded-2xl bg-orange-500 hover:bg-orange-600
                         text-white text-sm font-semibold transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              חזור להזמנות
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-5" dir="rtl">
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
          <p className="text-sm">טוען פרטי הזמנה…</p>
        </div>
      </div>
    );
  }

  // Error / not eligible
  if (loadError || !data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-5" dir="rtl">
        <div className="bg-white rounded-2xl border border-red-200 p-6 text-center space-y-4">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto" />
          <div className="space-y-1">
            <h1 className="text-base font-bold text-gray-900">לא ניתן לפתוח בקשת החזרה</h1>
            <p className="text-sm text-red-600">{loadError ?? 'הזמנה לא נמצאה'}</p>
          </div>
          <Link
            to="/b2b/orders"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl
                       bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium"
          >
            <ArrowRight className="w-4 h-4" />
            חזור להזמנות
          </Link>
        </div>
      </div>
    );
  }

  // Main form
  const allFullyReturned = data.lines.every(l => l.max_returnable_qty <= 0);

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-32" dir="rtl">

      {/* ── Header bar ────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <Link
          to="/b2b/orders"
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-500"
          aria-label="חזרה"
        >
          <ArrowRight className="w-5 h-5" />
        </Link>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2 flex-1">
          <RotateCcw className="w-5 h-5 text-orange-500" />
          בקשת החזרה
        </h1>
      </div>

      {/* ── Order summary card ──────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-orange-50 to-amber-50
                      rounded-2xl border border-orange-100 px-4 py-3.5 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-orange-700 font-semibold">
            <Hash className="w-3 h-3" />
            הזמנה #{shortId(data.header.order_id)}
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wide
                           text-green-700 bg-green-100 border border-green-200
                           px-2 py-0.5 rounded-full">
            סופקה
          </span>
        </div>

        <div className="flex items-center justify-between gap-2 text-[11px] text-gray-600">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3 text-gray-400" />
            {fmtDate(data.header.order_created_at)}
          </div>
          {data.header.company_name && (
            <div className="flex items-center gap-1.5 truncate">
              <ShoppingBag className="w-3 h-3 text-gray-400" />
              <span className="truncate">{data.header.company_name}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-1.5 border-t border-orange-100">
          <span className="text-[11px] text-gray-500">סה״כ הזמנה מקורי</span>
          <span className="text-sm font-bold text-gray-900">
            {fmtPrice(data.header.order_total_amount)}
          </span>
        </div>
      </div>

      {/* ── Info banner ────────────────────────────────────────────── */}
      <div className="flex items-start gap-2 px-3.5 py-2.5 bg-blue-50
                      border border-blue-100 rounded-xl text-[11px] text-blue-700">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <p>
          הזיכוי מחושב לפי המחיר ששולם בפועל בהזמנה זו, ולא לפי המחיר
          הנוכחי בקטלוג. ניתן להחזיר עד הכמות שסופקה לכל פריט.
        </p>
      </div>

      {/* ── Lines list ─────────────────────────────────────────────── */}
      {allFullyReturned ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center space-y-2">
          <Package className="w-8 h-8 text-gray-300 mx-auto" />
          <p className="text-sm font-semibold text-gray-700">לא ניתן להחזיר עוד פריטים</p>
          <p className="text-xs text-gray-500">כל הפריטים בהזמנה זו כבר הוחזרו.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide px-1">
            פריטי ההזמנה — בחר כמות להחזרה
          </h2>
          {data.lines.map(line => (
            <RMALineRow
              key={line.order_line_id}
              line={line}
              qty={selection[line.order_line_id]?.qty ?? 0}
              reason={selection[line.order_line_id]?.reason ?? ''}
              onQtyChange={setLineQty}
              onReasonChange={setLineReason}
            />
          ))}
        </div>
      )}

      {/* ── Global reason ──────────────────────────────────────────── */}
      {!allFullyReturned && (
        <div className="bg-white rounded-2xl border border-gray-100 p-3.5 space-y-2">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
            <MessageSquare className="w-3.5 h-3.5 text-orange-500" />
            סיבת החזרה כללית (אופציונלי)
          </label>
          <textarea
            value={globalReason}
            onChange={e => setGlobalReason(e.target.value)}
            placeholder="לדוגמה: הזמנה שגויה / פגום באריזה / לא תואם הזמנה…"
            rows={2}
            className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2
                       focus:outline-none focus:ring-2 focus:ring-orange-300
                       placeholder:text-gray-300 resize-none"
          />
        </div>
      )}

      {/* ── Submit error ───────────────────────────────────────────── */}
      {submitError && (
        <div className="flex items-start gap-2 px-3.5 py-2.5 bg-red-50
                        border border-red-200 rounded-xl text-xs text-red-700">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <p>{submitError}</p>
        </div>
      )}

      {/* ── Sticky submit footer ───────────────────────────────────── */}
      {!allFullyReturned && (
        <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur
                        border-t border-gray-200 px-4 py-3 z-40">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <div className="flex-1">
              <p className="text-[10px] text-gray-400 leading-tight">
                {totalQty > 0
                  ? `${totalQty} פריטים נבחרו להחזרה`
                  : 'בחר לפחות פריט אחד'}
              </p>
              <p className="text-base font-bold text-orange-600 tabular-nums">
                סה״כ זיכוי: {fmtPrice(totalCredit)}
              </p>
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-2xl
                         bg-orange-500 hover:bg-orange-600 active:bg-orange-700
                         text-white font-semibold text-sm transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed
                         active:scale-[.98] min-w-[140px]"
            >
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Send    className="w-4 h-4" />
              }
              {submitting ? 'שולח…' : 'שלח בקשת החזרה'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
